import type { Task } from "@app/core";
import { cn } from "@app/ui";
import { memo, useMemo, useState, type CSSProperties } from "react";
import { motion, springs, useReducedMotion, type MotionValue } from "../../lib/motion/index.js";
import { useTaskDialogStore } from "../../stores/index.js";
import { AllDayChip } from "./AllDayChip.js";
import { CalendarBackgroundMenu } from "./CalendarBackgroundMenu.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { computeAllDayRows, isMultiDay } from "./month-helpers.js";
import { useAllDayDrag, type AllDayTask, type ColInfo } from "./useAllDayDrag.js";

/** Chip-row geometry (mirrors the markup: h-5 rows, gap-px, p-0.5 column). */
const ROW_H = 20;
const ROW_GAP = 1;
const PAD_Y = 4;
/** The container's border-b sits INSIDE the box (border-box), so the explicit
 *  height must include it or the bottom row clips by a pixel. */
const BORDER_B = 1;
/** Desktop bottom breathing room so the last chip never sits flush against (or
 *  sub-pixel-clips under) the section's bottom border — generous enough to
 *  survive rounding at non-100% browser zoom. */
const BOTTOM_SLACK = 5;
/** Right strip (% of column) left clear on single-day chips so there's empty
 *  space to click and create a task — mirrors the timed grid's ~5% gap. */
const RIGHT_GAP = "5%";

interface Props {
  /** PADDED day set (same order as the timed track). */
  days: ColInfo[];
  allDayTaskMap: Map<string, AllDayTask>;
  axisWidthPx: number;
  /** Per-column width: a CSS "%" of the track (see TimeAxisGrid). */
  colWidth: string;
  /** Track width: a CSS "%" of the viewport. */
  trackWidth: string;
  /** Shared translateX ("% of track") so the all-day row pans with the grid. */
  x: MotionValue<string>;
  /** Index (into `days`) of the first column currently in view. */
  firstVisibleCol: number;
  /** How many columns are visible — the section sizes to THESE columns' rows. */
  visibleCount: number;
  /** Phone: drop the "all day" label text (the slim axis has no room for it). */
  compact?: boolean;
}

function AllDayRowImpl({ days, allDayTaskMap, axisWidthPx, colWidth, trackWidth, x, firstVisibleCol, visibleCount, compact = false }: Props) {
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const reduce = useReducedMotion();
  const {
    startCreate,
    startMove,
    startResizeLeft,
    startResizeRight,
    onColEnter,
    onNativeDragOver,
    onNativeDragLeave,
    onNativeDrop,
    isHighlighted,
  } =
    useAllDayDrag(days, allDayTaskMap);

  const [bgCtx, setBgCtx] = useState<{ x: number; y: number; col: number } | null>(null);

  const { visibleMaxRow, tasksByRowByCol } = useMemo(() => {
    const allUniqueTasks = Array.from(allDayTaskMap.values(), (value) => value.task);
    // Row assignment stays GLOBAL (over the whole window) so a multi-day event
    // keeps the same row index across columns as it pans into view.
    const rowMap = computeAllDayRows(allUniqueTasks);
    const tasksByRowByCol = days.map((day) => {
      const tasksByRow = new Map<number, Task>();
      for (const task of day.tasks) {
        if (task.all_day || !task.due_at) tasksByRow.set(rowMap.get(task.id) ?? 0, task);
      }
      return tasksByRow;
    });
    // Tallest stack among the columns currently IN VIEW — days outside the view
    // (buffer-only events, or a busier day off-screen) don't inflate the section.
    let visibleMaxRow = -1;
    const from = Math.max(0, firstVisibleCol);
    const to = Math.min(days.length, firstVisibleCol + visibleCount);
    for (let i = from; i < to; i++) {
      for (const row of tasksByRowByCol[i]!.keys()) if (row > visibleMaxRow) visibleMaxRow = row;
    }
    return { visibleMaxRow, tasksByRowByCol };
  }, [allDayTaskMap, days, firstVisibleCol, visibleCount]);

  // The section sizes to the VISIBLE columns' tallest stack so it never extends
  // past what the in-view days need, animating the height change on BOTH
  // platforms. Desktop keeps a 44px floor (its old per-column min height).
  const rowCount = Math.max(1, visibleMaxRow + 1);
  const contentHeight = PAD_Y + rowCount * ROW_H + (rowCount - 1) * ROW_GAP;
  const base = compact ? contentHeight : Math.max(44, contentHeight) + BOTTOM_SLACK;
  const height = base + BORDER_B;
  const compactColStyle = useMemo<CSSProperties | undefined>(
    () => compact ? { width: colWidth, contentVisibility: "auto", containIntrinsicSize: `1px ${height}px` } : undefined,
    [colWidth, compact, height],
  );

  const content = (
    <>
      <div style={{ width: axisWidthPx }} className="shrink-0 border-r p-1 text-right text-[calc(10px*var(--app-text-scale))] uppercase text-muted-foreground">{compact ? "" : "all day"}</div>
      <div className="relative h-full flex-1 overflow-hidden">
        <motion.div className="flex h-full" style={{ x, width: trackWidth, willChange: "transform" }}>
          {days.map((d, i) => {
            const tasksByRow = tasksByRowByCol[i] ?? new Map<number, Task>();
            return (
              <div
                key={i}
                data-all-day-col={i}
                style={compactColStyle ?? { width: colWidth }}
                className={cn(
                  // Horizontal padding intentionally dropped (py only) so chips
                  // align edge-to-edge with the timed blocks below; the right
                  // click-strip comes from each chip's RIGHT_GAP margin instead.
                  "h-full flex-none cursor-pointer select-none border-r py-0.5",
                  isHighlighted(i) ? "bg-primary/15" : "hover:bg-accent/30",
                )}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  if ((e.target as HTMLElement).closest("[data-chip]")) return;
                  startCreate(e, i);
                }}
                onMouseEnter={() => onColEnter(i)}
                onDragOver={(e) => onNativeDragOver(e, i)}
                onDragLeave={(e) => onNativeDragLeave(e, i)}
                onDrop={(e) => onNativeDrop(e, i)}
                onContextMenu={(e) => {
                  if (suppressMobileContextMenu(e)) return;
                  if ((e.target as HTMLElement).closest("[data-chip]")) return;
                  e.preventDefault();
                  setBgCtx({ x: e.clientX, y: e.clientY, col: i });
                }}
              >
                <div className="flex flex-col gap-px">
                  {Array.from({ length: rowCount }, (_, row) => {
                    const task = tasksByRow.get(row);
                    if (!task) return <div key={row} className="h-5" />;
                    const span = allDayTaskMap.get(task.id);
                    const isFirst = span ? span.startCol === i : true;
                    const isLast = span ? span.endCol === i : true;
                    return (
                      <AllDayChip
                        key={task.id}
                        task={task}
                        isFirst={isFirst}
                        isLast={isLast}
                        date={d.date}
                        overlapPx={3}
                        canResizeLeft={isMultiDay(task)}
                        canResizeRight={task.all_day}
                        rightGap={RIGHT_GAP}
                        onClick={() => openEdit(task)}
                        onMoveStart={(e) => startMove(e, task, i)}
                        onResizeLeft={(e) => startResizeLeft(e, task)}
                        onResizeRight={(e) => startResizeRight(e, task)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
      {bgCtx && (
        <CalendarBackgroundMenu
          x={bgCtx.x}
          y={bgCtx.y}
          onClose={() => setBgCtx(null)}
          onCreate={() => { const day = days[bgCtx.col]?.date; if (day) openCreate(day, 1440); }}
        />
      )}
    </>
  );

  return (
    <motion.div
      className="flex shrink-0 overflow-hidden border-b"
      animate={{ height }}
      initial={false}
      transition={reduce ? { duration: 0 } : springs.gentle}
    >
      {content}
    </motion.div>
  );
}

export const AllDayRow = memo(AllDayRowImpl);
